import { useCallback } from "react";

import { useRootLayoutData } from "~/root";
import { resolveImageHost } from "~/utils/salesforce-image-transform";

import type { Image } from "../lib/app.types";

const MEDIA = {
  plp: ["$plp_pwa_desktop$", ""] as const,
  pdp: ["$pdp_desktop_pwa$", ""] as const,
  thumb: ["$pdp_thumb$", ""] as const,
} as const;

const isMedia = (type: string): type is keyof typeof MEDIA => {
  return type in MEDIA;
};

const useImageUtils = () => {
  const { IMAGES_HOST, IMAGE_FOLDER_ID } = useRootLayoutData();
  const baseUrl = IMAGES_HOST || "";
  const imageFolderId = IMAGE_FOLDER_ID || "";

  const getImageLink = useCallback(
    // Re-host onto IMAGES_HOST. Relative paths get the host prepended;
    // absolute SCAPI links on the instance/realm host (which 403s + ORB-blocks)
    // are re-pointed at the storefront host. Non-SFCC links pass through.
    // When a DIS folder id is set, SFCC static paths are upgraded to the
    // `/dw/image/v2/…` route so `sw`/`q`/… transforms are actually applied.
    (imageLink: string) =>
      resolveImageHost(imageLink, baseUrl, imageFolderId) ?? "",
    [baseUrl, imageFolderId],
  );

  const getImageMediaLink = useCallback(
    (
      image: Image,
      {
        type,
        quality,
        params,
      }: {
        type?: keyof typeof MEDIA;
        quality?: number;
        params?: Record<string, string>;
      },
    ): string => {
      const rawLink = getImageLink(image.disBaseLink || image.link);
      // A product with no image (both links empty) yields "" and a non-absolute
      // link yields a relative path — `new URL()` throws on either, which would
      // crash the entire PLP/PDP SSR stream over a single bad tile. Bail to the
      // raw value (empty string, or a same-origin-resolvable relative path) so
      // the image just fails to load instead of taking down the whole route.
      if (!rawLink) return "";
      let url: URL;
      try {
        url = new URL(rawLink);
      } catch {
        return rawLink;
      }
      let searchParams = url.searchParams;

      if (type && isMedia(type) && MEDIA[type]) {
        url.searchParams.set(MEDIA[type][0], MEDIA[type][1]);
      }
      if (quality) url.searchParams.set("qlt", quality.toString());
      if (params) {
        searchParams = new URLSearchParams({
          ...Object.fromEntries(searchParams),
          ...params,
        });
      }

      return `${url.origin}${url.pathname}?${searchParams.toString()}`;
    },
    [getImageLink],
  );
  return {
    getImageLink,
    getImageMediaLink,
  };
};

export default useImageUtils;
